import { MemberStatus } from '~/services/team/interface'

// The GET /me query key. Lives here rather than in AuthContext (its sole
// consumer today) so a future cache invalidation — e.g. on org switch — has a
// stable import path that doesn't reach into the context module.
export const myPermissionsQueryKey = ['my-permissions']

export const DEFAULT_PAGE_LIMIT = 20

export const OPTIONS_PAGINATION_LIMIT = [10, 20, 50]

export const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'key_status_all' },
  { value: 'active', label: 'key_status_active' },
  { value: 'pending_activation', label: 'key_status_inactive' },
  { value: 'disabled', label: 'key_status_disabled' },
] as const

interface MemberStatusChipStyle {
  labelKey: string
  bgColor: string
  textColor: string
}

export const MEMBER_STATUS_CHIP: Record<MemberStatus, MemberStatusChipStyle> =
  {
    active: {
      labelKey: 'key_status_active',
      bgColor: 'rgba(84, 214, 44, 0.16)',
      textColor: 'success.dark',
    },
    pending_activation: {
      labelKey: 'key_status_inactive',
      bgColor: 'rgba(255, 72, 66, 0.16)',
      textColor: 'error.dark',
    },
    disabled: {
      labelKey: 'key_status_disabled',
      bgColor: 'rgba(145, 158, 171, 0.16)',
      textColor: 'text.secondary',
    },
  }

interface RoleAvatarStyle {
  icon: string
  bgColor: string
}

// Filled (baseline) glyphs, not outline: Figma node 108-34467 draws these as
// solid shapes on a pale disc of the same hue. The icons differ from ROLE_ICON
// below on purpose — the table avatar shows Admin as person-with-gear and
// Operations as a plain person, where the Team Overview legend uses a gear and
// a wrench. Both match their own frame.
const ROLE_AVATAR_STYLE: Record<string, RoleAvatarStyle> = {
  owner: { icon: 'ic:baseline-stars', bgColor: 'primary.lighter' },
  admin: { icon: 'ic:baseline-manage-accounts', bgColor: 'info.lighter' },
  finance: {
    icon: 'ic:baseline-account-balance-wallet',
    bgColor: 'success.lighter',
  },
  operations: { icon: 'ic:baseline-person', bgColor: 'warning.lighter' },
  viewer: { icon: 'ic:baseline-remove-red-eye', bgColor: 'grey.200' },
}

const DEFAULT_ROLE_AVATAR_STYLE: RoleAvatarStyle = {
  icon: 'ic:baseline-person',
  bgColor: 'grey.200',
}

export const getRoleAvatarStyle = (roleName: string): RoleAvatarStyle =>
  ROLE_AVATAR_STYLE[roleName?.toLowerCase()] || DEFAULT_ROLE_AVATAR_STYLE

const ROLE_ICON: Record<string, string> = {
  owner: 'ic:baseline-stars',
  admin: 'ic:baseline-settings',
  finance: 'ic:baseline-account-balance-wallet',
  operations: 'ic:baseline-construction',
  viewer: 'ic:baseline-remove-red-eye',
}

const DEFAULT_ROLE_ICON = 'ic:baseline-person'

export const getRoleIcon = (roleName: string): string =>
  ROLE_ICON[roleName?.toLowerCase()] || DEFAULT_ROLE_ICON

// Icon fill per role, used both by the Team Overview legend and by the member
// table's avatar. Admin/Finance/Operations match info.darker/success.darker/
// warning.darker exactly. primary.darker now resolves to #680B2E, the maroon
// Figma asks for — it used to equal primary.main, which is why the token was
// chosen over a literal; that theme bug is fixed. grey.700 is close to Figma's
// viewer color but not pixel-identical; using the token rather than a one-off
// hex.
const ROLE_ICON_COLOR: Record<string, string> = {
  owner: 'primary.darker',
  admin: 'info.darker',
  finance: 'success.darker',
  operations: 'warning.darker',
  viewer: 'grey.700',
}

const DEFAULT_ROLE_ICON_COLOR = 'grey.700'

export const getRoleIconColor = (roleName: string): string =>
  ROLE_ICON_COLOR[roleName?.toLowerCase()] || DEFAULT_ROLE_ICON_COLOR

// Fixed display order + capitalization per the confirmed Figma frame
// (node 110-37567) — the roles API returns lowercase names in whatever
// order the backend defines, not this display order.
const ROLE_DISPLAY_ORDER = ['owner', 'admin', 'finance', 'operations', 'viewer']

const ROLE_DISPLAY_NAME: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  finance: 'Finance',
  operations: 'Operator',
  viewer: 'Viewer',
}

export const getRoleDisplayName = (roleName: string): string =>
  ROLE_DISPLAY_NAME[roleName?.toLowerCase()] || roleName

export const sortRolesByDisplayOrder = <T extends { name: string }>(
  roles: T[],
): T[] =>
  [...roles].sort(
    (a, b) =>
      ROLE_DISPLAY_ORDER.indexOf(a.name.toLowerCase()) -
      ROLE_DISPLAY_ORDER.indexOf(b.name.toLowerCase()),
  )
